'use client'

import React from 'react'
import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Facebook, FileAxis3D, Instagram, Mail, MapPin, Phone } from 'lucide-react'
import { motion } from 'motion/react'
import { useInView } from 'react-intersection-observer'
import Image from 'next/image'
import { cn } from '@/utilities/ui'
import { Mask } from '@/components/Mask'
import { CMSLink } from '@/components/Link'
// import NewsletterWidget from '@/components/NewsletterWidget'
import { TypedLocale } from 'payload'

interface FooterClientProps {
  navLinks: any[]
  linkFacebook: string | null
  linkInstagram: string | null
  description: string | null
  locale: TypedLocale
}

export function FooterClient({
  navLinks,
  linkFacebook,
  linkInstagram,
  description,
  locale,
}: FooterClientProps) {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
    },
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
    },
    transition: {
      duration: 0.8,
      ease: 'easeOut',
    },
  }

  const bottomVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
    },
    transition: {
      delay: 0.8,
      duration: 0.8,
    },
  }

  return (
    <footer className="relative overflow-hidden bg-antonie-brown pb-20 text-white">
      <motion.div
        ref={ref}
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={containerVariants}
        className="container mx-auto px-6 py-20"
      >
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4">
          <motion.div variants={itemVariants}>
            <div className="mb-8 flex flex-col items-start gap-4">
              <Image
                src="/logo_antonie.svg"
                alt="Hotel Logo"
                width={40}
                height={40}
                className={cn(
                  'w-auto transition-all duration-300 h-10 brightness-0 invert',
                  // isScrolled
                  //   ? 'h-12 brightness-0  '
                  //   : dark
                  //     ? 'h-20 brightness-0'
                  //     : 'h-20 brightness-0 invert',
                )}
              />
              <span className="text-shadow-lg/10 font-serif text-xl tracking-wide">
                Bergsporthotel Antonie ***s
              </span>
            </div>
            {description && (
              <p className="mb-8 font-light leading-relaxed text-gray-300">{description}</p>
            )}
            <div className="flex space-x-4">
              {linkFacebook && (
                <Link href={linkFacebook}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-gray-300 transition-colors duration-300 hover:text-antonie-green"
                    aria-label="Facebook"
                  >
                    <Facebook className="h-5 w-5" />
                  </Button>
                </Link>
              )}
              {linkInstagram && (
                <Link href={linkInstagram}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-gray-300 transition-colors duration-300 hover:text-antonie-green"
                    aria-label="Instagram"
                  >
                    <Instagram className="h-5 w-5" />
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <h3 className="mb-8 font-serif text-lg font-light tracking-wide">Links</h3>
            <nav className="flex flex-col gap-4">
              {navLinks.map(({ link }, i) => {
                return (
                  <CMSLink
                    className="text-gray-300 hover:text-primary"
                    key={i}
                    {...link}
                    appearance="link"
                  />
                )
              })}
            </nav>
          </motion.div>

          <motion.div variants={itemVariants}>
            <h3 className="mb-8 font-serif text-lg font-light tracking-wide">
              {locale === 'de' ? 'Kontakt' : 'Contact'}
            </h3>
            <ul className="space-y-5">
              <motion.li
                className="flex"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <MapPin className="mr-3 h-5 w-5 shrink-0 text-antonie-green" />
                <span className="font-light text-gray-300">Gries 16 – 6182 Gries im Sellrain</span>
              </motion.li>
              <motion.li
                className="flex"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Phone className="mr-3 h-5 w-5 shrink-0 text-antonie-green" />
                <span className="font-light text-gray-300">+43 (0)5236 203</span>
              </motion.li>
              <motion.li
                className="flex"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <FileAxis3D className="mr-3 h-5 w-5 shrink-0 text-antonie-green" />
                <span className="font-light text-gray-300">+43 (0)5236 203-49</span>
              </motion.li>
              <motion.li
                className="flex"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Mail className="mr-3 h-5 w-5 shrink-0 text-antonie-green" />
                <span className="font-light text-gray-300">info@hotel-antonie.at</span>
              </motion.li>
            </ul>
          </motion.div>

          {/* <NewsletterWidget /> */}
          {/* <motion.div variants={itemVariants}>
            <h3 className="mb-8 font-serif text-lg font-light tracking-wide">Newsletter</h3>
            <p className="mb-6 font-light text-gray-300">
              Abonnieren Sie unseren Newsletter und erhalten Sie exklusive Angebote.
            </p>
            <div className="space-y-4">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <Input
                  type="email"
                  placeholder="Ihre E-Mail-Adresse"
                  className="h-12 border-2 border-gray-700 bg-white px-5 text-black"
                />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <Button className="w-full bg-antonie-red text-white transition-all duration-300 hover:bg-antonie-red-hover">
                  Abonnieren
                </Button>
              </motion.div>
            </div>
          </motion.div> */}
        </div>

        <motion.div
          variants={bottomVariants}
          className="mt-16 border-t border-gray-800 pt-10 text-center text-sm text-gray-300"
        >
          <div className="mb-6 flex flex-col justify-center gap-6 md:flex-row">
            <Link
              href="/impressum"
              className="font-light transition-colors duration-300 hover:text-antonie-green"
            >
              {locale === 'de' ? 'Impressum' : 'Imprint'}
            </Link>
            <Link
              href="/datenschutz"
              className="font-light transition-colors duration-300 hover:text-antonie-green"
            >
              {locale === 'de' ? 'Datenschutz' : 'Privacy Policy'}
            </Link>
            <Link
              href="/agb"
              className="font-light transition-colors duration-300 hover:text-antonie-green"
            >
              {locale === 'de' ? 'AGB' : 'Terms and Conditions'}
            </Link>
            <Link
              href="/cookies"
              className="font-light transition-colors duration-300 hover:text-antonie-green"
            >
              {locale === 'de' ? 'Cookie-Richtlinie' : 'Cookie Policy'}
            </Link>
          </div>
          <p className="font-light">
            © {new Date().getFullYear()} Hotel Antonie.{' '}
            {locale === 'de' ? 'Alle Rechte vorbehalten.' : 'All rights reserved.'}
          </p>
        </motion.div>
      </motion.div>
      <div className="absolute bottom-0 right-0 z-20">
        <Mask
          width={500}
          height={500}
          color="white"
          className="h-[250px] w-[250px] translate-x-1/4 translate-y-1/4 md:h-[500px] md:w-[500px]"
        />
      </div>
    </footer>
  )
}

export default FooterClient
